import React, { useState, useMemo } from "react";
import { X, Bell, CheckCircle, AlertTriangle, Users, Send, RefreshCw, Search, Filter, ChevronDown } from "lucide-react";

const ExtensionNotificationModal = ({
  onClose,
  getExtensionPendingCandidates,
  getDaysRemaining,
  sendAccountManagerNotification,
  error,
  success,
}) => {
  const pendingCandidates = getExtensionPendingCandidates();
  const [selectedIds, setSelectedIds] = useState([]);
  const [sending, setSending] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [buFilter, setBuFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Unique values for dropdowns
  const uniqueBUs = useMemo(() => [
    'all',
    ...new Set(pendingCandidates.map(c => c.buHead).filter(Boolean))
  ], [pendingCandidates]);

  const uniqueClients = useMemo(() => [
    'all',
    ...new Set(pendingCandidates.map(c => c.client).filter(Boolean))
  ], [pendingCandidates]);

  // Filtered candidates
  const filteredCandidates = useMemo(() => {
    return pendingCandidates.filter(candidate => {
      const days = getDaysRemaining(candidate);

      // Search — name, email, client, BU
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const matches =
          candidate.candidateName?.toLowerCase().includes(s) ||
          candidate.candidateEmail?.toLowerCase().includes(s) ||
          candidate.client?.toLowerCase().includes(s) ||
          candidate.buHead?.toLowerCase().includes(s) ||
          candidate.jobTitle?.toLowerCase().includes(s);
        if (!matches) return false;
      }

      // BU filter
      if (buFilter !== 'all' && candidate.buHead !== buFilter) return false;

      // Client filter
      if (clientFilter !== 'all' && candidate.client !== clientFilter) return false;

      // Urgency filter based on days remaining
      if (urgencyFilter === 'overdue' && days >= 0) return false;
      if (urgencyFilter === 'critical' && (days < 0 || days > 7)) return false;
      if (urgencyFilter === 'soon' && (days < 0 || days > 30)) return false;
      if (urgencyFilter === 'upcoming' && days <= 30) return false;

      return true;
    });
  }, [pendingCandidates, searchTerm, buFilter, clientFilter, urgencyFilter, getDaysRemaining]);

  const getUrgencyBadge = (days) => {
    if (days < 0) return "bg-purple-100 text-purple-800 border-purple-200";
    if (days <= 7) return "bg-red-100 text-red-800 border-red-200";
    if (days <= 30) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-blue-100 text-blue-800 border-blue-200";
  };

  const getUrgencyLabel = (days) => {
    if (days < 0) return `${Math.abs(days)}d overdue`;
    if (days === 0) return 'Today';
    return `${days}d left`;
  };

  const handleToggle = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const filteredIds = filteredCandidates.map(c => c.id);
    const allSelected = filteredIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const filteredSelectedCount = filteredCandidates.filter(c => selectedIds.includes(c.id)).length;
  const allFilteredSelected = filteredCandidates.length > 0 &&
    filteredCandidates.every(c => selectedIds.includes(c.id));

  const clearFilters = () => {
    setSearchTerm('');
    setBuFilter('all');
    setClientFilter('all');
    setUrgencyFilter('all');
  };

  const hasActiveFilters = searchTerm || buFilter !== 'all' || clientFilter !== 'all' || urgencyFilter !== 'all';

  const handleSend = async () => {
    if (selectedIds.length === 0) {
      error("Please select at least one candidate.");
      return;
    }
    const selectedCandidates = pendingCandidates.filter(c => selectedIds.includes(c.id));
    try {
      setSending(true);
      await sendAccountManagerNotification(selectedCandidates, 'account_manager_rolloff_notification');
      success(`Notifications sent for ${selectedCandidates.length} candidate(s).`);
      onClose();
    } catch (err) {
      error("Failed to send notifications.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Bell className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Notify Account Managers</h2>
                <p className="text-orange-100 text-sm">
                  {pendingCandidates.length} candidate(s) pending extension decision
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search + Filter Bar */}
        {pendingCandidates.length > 0 && (
          <div className="px-6 pt-4 pb-3 border-b border-slate-100 flex-shrink-0 space-y-3">

            {/* Search */}
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, client, BU..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  hasActiveFilters
                    ? 'bg-orange-50 text-orange-700 border-orange-300'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                <Filter className="w-3.5 h-3.5" />
                Filters
                {hasActiveFilters && (
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                )}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-lg border border-red-200 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="grid grid-cols-3 gap-2">
                {/* BU Filter */}
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Business Unit</label>
                  <select
                    value={buFilter}
                    onChange={e => setBuFilter(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    {uniqueBUs.map(bu => (
                      <option key={bu} value={bu}>{bu === 'all' ? 'All BUs' : bu}</option>
                    ))}
                  </select>
                </div>

                {/* Client Filter */}
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Client</label>
                  <select
                    value={clientFilter}
                    onChange={e => setClientFilter(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    {uniqueClients.map(c => (
                      <option key={c} value={c}>{c === 'all' ? 'All Clients' : c}</option>
                    ))}
                  </select>
                </div>

                {/* Urgency Filter */}
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Urgency</label>
                  <select
                    value={urgencyFilter}
                    onChange={e => setUrgencyFilter(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value="all">All</option>
                    <option value="overdue">Overdue</option>
                    <option value="critical">Critical (≤7 days)</option>
                    <option value="soon">Soon (≤30 days)</option>
                    <option value="upcoming">Upcoming (30+ days)</option>
                  </select>
                </div>
              </div>
            )}

            {/* Results count */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Showing <span className="font-semibold text-slate-700">{filteredCandidates.length}</span> of {pendingCandidates.length} candidates
              </p>
              {hasActiveFilters && filteredCandidates.length === 0 && (
                <p className="text-xs text-orange-600 font-medium">No matches — try adjusting filters</p>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {pendingCandidates.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">All caught up!</h3>
              <p className="text-slate-500">No candidates pending extension decision.</p>
            </div>
          ) : (
            <div className="space-y-3">

              {/* Warning Banner */}
              <div className="flex items-start space-x-3 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-orange-800">Extension decision required</p>
                  <p className="text-xs text-orange-700 mt-0.5">
                    Select candidates below and click Send to notify their assigned account managers via email.
                  </p>
                </div>
              </div>

              {/* Select All Row */}
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-semibold text-slate-700">
                    Select All ({filteredCandidates.length})
                  </span>
                </label>
                {selectedIds.length > 0 && (
                  <span className="text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 px-2 py-1 rounded-full">
                    {selectedIds.length} selected total
                  </span>
                )}
              </div>

              {/* Candidate List */}
              {filteredCandidates.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No candidates match your filters.
                </div>
              ) : (
                filteredCandidates.map(candidate => {
                  const daysRemaining = getDaysRemaining(candidate);
                  const isSelected = selectedIds.includes(candidate.id);

                  return (
                    <label
                      key={candidate.id}
                      className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-orange-50 border-orange-300'
                          : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggle(candidate.id)}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{candidate.candidateName}</p>
                          <p className="text-xs text-slate-600 truncate">{candidate.client} — {candidate.jobTitle}</p>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <p className="text-xs text-slate-500">BU: {candidate.buHead || 'Not assigned'}</p>
                            {candidate.sowEndDate && (
                              <p className="text-xs text-slate-500">
                                SOW: {new Date(candidate.sowEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                            {candidate.account_manager_1 && (
                              <p className="text-xs text-blue-600 truncate">AM: {candidate.account_manager_1}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getUrgencyBadge(daysRemaining)}`}>
                          {getUrgencyLabel(daysRemaining)}
                        </span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center space-x-2 text-sm text-slate-600">
            <Users className="w-4 h-4 text-slate-400" />
            <span>{selectedIds.length} of {pendingCandidates.length} selected</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-white transition-colors text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={selectedIds.length === 0 || sending}
              className="px-6 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-colors font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {sending ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send to {selectedIds.length} Candidate(s)</span>
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ExtensionNotificationModal;